import {
  ExpandMore as ExpandMoreIcon,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Grid, IconButton, Typography } from '@mui/material';
import { set } from 'lodash';
import { useMemo } from 'react';
import type { Path } from 'react-hook-form';
import { NumberInputWidget } from 'src/components/widget';
import { type IGcfOutageStamp, IProduct } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  BackSoonAccordion,
  BackSoonAccordionDetails,
  BackSoonAccordionSummary,
  CustomStyledAlert,
} from './BackSoon.styled';

interface IBackSoonProduct extends IProduct {
  active: boolean;
}

interface IBackSoonAccordion {
  product: IBackSoonProduct | undefined;
  outageStamp?: IGcfOutageStamp;
  supportingProductCode?: string;
  onChange: (value: IGcfOutageStamp) => void;
  onChangeBackSoonActive: (product?: string, active?: boolean) => void;
}

export const BackSoonAccordionPanel = ({
  product,
  outageStamp,
  onChange,
  onChangeBackSoonActive,
  supportingProductCode,
}: IBackSoonAccordion) => {
  const handleOutageStampChanges =
    (field: Path<IGcfOutageStamp>) => (value: any) => {
      const newOutageStamp = {
        ...outageStamp,
        visible: true,
      } as IGcfOutageStamp;

      onChange(set(newOutageStamp, field, value));
    };

  const isProductActive = useMemo(
    () =>
      supportingProductCode
        ? product?.active &&
          product?.productVariations.find(
            (item) => item.product_code === supportingProductCode,
          )?.active
        : product?.active &&
          product?.productVariations.some((item) => item.active),

    [supportingProductCode, product],
  );

  return (
    <BackSoonAccordion>
      <BackSoonAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">{lang.backSoonLabel}</Typography>

        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onChangeBackSoonActive(product?.product_code, !isProductActive);
          }}>
          <VisibilityOutlined
            color={isProductActive ? 'secondary' : 'primary'}
          />
        </IconButton>
      </BackSoonAccordionSummary>
      <BackSoonAccordionDetails>
        <CustomStyledAlert icon={false}>{lang.backSoonText}</CustomStyledAlert>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.width}
              value={outageStamp?.style?.width}
              onChange={handleOutageStampChanges('style.width')}
              options={{ suffix: 'px' }}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.height}
              value={outageStamp?.style?.height}
              onChange={handleOutageStampChanges('style.height')}
              options={{ suffix: 'px' }}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.leftPosition}
              value={outageStamp?.style?.left}
              onChange={handleOutageStampChanges('style.left')}
              options={{ suffix: 'px' }}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.topPosition}
              value={outageStamp?.style?.top}
              onChange={handleOutageStampChanges('style.top')}
              options={{ suffix: 'px' }}
            />
          </Grid>
        </Grid>
      </BackSoonAccordionDetails>
    </BackSoonAccordion>
  );
};
